import CryptoJS from 'crypto-js'
import {
  ACKNOWLEDGE_GLOBAL_MESSAGE,
  DEVICE_NOTIFY_FAILURE,
  DEVICE_NOTIFY_REQUEST,
  DEVICE_NOTIFY_SUCCESS,
  DISMISS_ERROR,
  FETCH_LANGUAGES_SUCCESS,
  FETCH_SETTINGS_FAILURE,
  FETCH_SETTINGS_REQUEST,
  FETCH_SETTINGS_SUCCESS,
  FETCH_TRANSLATIONS_FAILURE,
  FETCH_TRANSLATIONS_REQUEST,
  FETCH_TRANSLATIONS_SUCCESS,
  HIDE_TOAST,
  INIT_APP,
  SET_CURRENT_LANGUAGE,
  SHOW_TOAST,
  FETCH_PRIVACY_POLICY_REQUEST,
  FETCH_PRIVACY_POLICY_SUCCESS,
  FETCH_PRIVACY_POLICY_FAILURE,
  FETCH_TERMS_OF_USE_REQUEST,
  FETCH_TERMS_OF_USE_SUCCESS,
  FETCH_TERMS_OF_USE_FAILURE,
} from 'actions/app'
import { LOGIN_SUCCESS } from 'actions/login'
import { LOGOUT_SUCCESS } from 'actions/logout'
import { GLOBAL_MESSAGE_LAST_UPDATED_KEY } from 'constants/app'
import auth from 'libs/auth'

const INITIAL_STATE = {
  translationsReady: false,
  settingsIsReady: false,
  notifyIsReady: false,
  error: false,
  errorMessage: '',
  availableLanguages: [],
  dictionary: {},
  mainMenu: {},
  modules: {},
  playerTimeout: 0,
  isLoggedIn: auth.isLoggedIn(),
  toastMessage: '',
  toastType: 'success',
  appName: '',
  appRoot: '',
  allowYouTube: false,
  globalMessage: null,
  forceLogin: false,
  removeRegister: false,
  previewDuration: 0,
  previewTriggerLength: 0,
  tagManagerId: '',
  analyticsInterval: 300, // 5 minutes, in seconds
  appBundleTemplate: '',
  // privacy policy
  privacyPolicyReady: false,
  privacyPolicyError: false,
  privacyPolicyContent: '',
  // terms of use
  termsOfUseReady: false,
  termsOfUseError: false,
  termsOfUseContent: '',
  // default route
  defaultRoute: 'recommendations',
  sidebar: true,
  accountGA: '',
  facebookAppId: '',
  facebookLoginEnabled: 'no',
  captchaId: '',
  twitterLoginEnabled: 'no',
  twitterCallback: '',
  twitterConsumerKey: '',
  twitterConsumerSecret: '',
}

const appReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case INIT_APP:
      return {
        ...state,
        settingsIsReady: false,
        notifyIsReady: false,
        error: false,
        errorMessage: '',
      }
    case DEVICE_NOTIFY_REQUEST:
      return {
        ...state,
        notifyIsReady: action.payload.resetReady ? false : state.notifyIsReady,
      }
    case DEVICE_NOTIFY_SUCCESS:
      return {
        ...state,
        notifyIsReady: true,
      }
    case DEVICE_NOTIFY_FAILURE:
      return {
        ...state,
        notifyIsReady: true,
        error: true,
        errorMessage: action.payload.message,
      }
    case FETCH_SETTINGS_REQUEST:
      return {
        ...state,
        settingsIsReady: false,
      }
    case FETCH_SETTINGS_SUCCESS:
      return {
        ...state,
        settingsIsReady: true,
        dictionary: action.payload.settings.dictionary,
        mainMenu: action.payload.settings.main_menu.items.map(item => ({
          id: +item['id'],
          name: item['name'],
          icon: item['icon'],
          actionTarget: item['action_target'],
          actionId: +item['action_id'],
          actionUrl: item['action_url'],
        })),
        modules: action.payload.settings.modules,
        playerTimeout: +action.payload.settings.playerTimeout,
        appName: action.payload.settings.app_name || '',
        appRoot: action.payload.settings.www_root || '',
        allowYouTube: !!action.payload.settings.allow_youtube,
        globalMessage:
          typeof action.payload.settings.globalMessage === 'undefined'
            ? null
            : {
                title: action.payload.settings.globalMessage.title,
                body: action.payload.settings.globalMessage.content,
                lastModified: action.payload.settings.globalMessage.last_modified * 1000,
                lastDisplayed: +localStorage.getItem(GLOBAL_MESSAGE_LAST_UPDATED_KEY),
              },
        forceLogin: action.payload.settings.force_authentication === 'yes',
        removeRegister: !!action.payload.settings.remove_register,
        previewDuration:
          typeof action.payload.settings.previews !== 'undefined'
            ? action.payload.settings.previews.duration
            : 0,
        previewTriggerLength:
          typeof action.payload.settings.previews !== 'undefined'
            ? action.payload.settings.previews.trigger_length
            : 0,
        tagManagerId:
          typeof action.payload.settings.google_tag_manager_id !== 'undefined'
            ? action.payload.settings.google_tag_manager_id
            : '',
        analyticsInterval:
          typeof action.payload.settings.analytics_interval !== 'undefined'
            ? +action.payload.settings.analytics_interval
            : 300,
        appBundleTemplate:
          typeof action.payload.settings.app_bundle_template !== 'undefined'
            ? action.payload.settings.app_bundle_template
            : '',
        defaultRoute:
          typeof action.payload.settings.default_route !== 'undefined'
            ? action.payload.settings.default_route
            : 'recommendations',
        accountGA: action.payload.settings.google_analytics_account || '',
        facebookLoginEnabled: action.payload.settings.facebook_login_enabled || 'no',
        facebookAppId: action.payload.settings.facebook_app_id || '',
        captchaId: action.payload.settings.captcha_client_side || '',
        twitterLoginEnabled: action.payload.settings.twitter_login_enabled || 'no',
        twitterCallback: action.payload.settings.twitter_callback || '',
        twitterConsumerKey: action.payload.settings.twitter_consumer_key || '',
        twitterConsumerSecret: () => {
          const secretKey = action.payload.settings.twitter_consumer_secret || ''
          const encodedSecretKey = CryptoJS.enc.Base64.parse(secretKey)
          const decodedSecretKey = CryptoJS.enc.Utf8.stringify(encodedSecretKey)
          const reversedKey = decodedSecretKey
            .split('')
            .reverse()
            .join('')

          return reversedKey
        },
      }
    case FETCH_SETTINGS_FAILURE:
      return {
        ...state,
        settingsIsReady: true,
        error: true,
        errorMessage: action.payload.message,
      }

    case FETCH_TRANSLATIONS_REQUEST:
      return {
        ...state,
        translationsReady: false,
      }
    case FETCH_TRANSLATIONS_SUCCESS:
      return {
        ...state,
        translationsReady: true,
      }
    case FETCH_TRANSLATIONS_FAILURE:
      return {
        ...state,
        translationsReady: true,
      }

    case SHOW_TOAST:
      return {
        ...state,
        toastMessage: action.payload.message,
        toastType: action.payload.type,
      }
    case HIDE_TOAST:
      return {
        ...state,
        toastMessage: '',
        toastType: 'success',
      }

    case DISMISS_ERROR:
      return {
        ...INITIAL_STATE,
        isLoggedIn: auth.isLoggedIn(),
      }

    case LOGIN_SUCCESS:
    case LOGOUT_SUCCESS:
      return {
        ...state,
        isLoggedIn: auth.isLoggedIn(),
      }

    case FETCH_LANGUAGES_SUCCESS:
      return {
        ...state,
        availableLanguages: action.payload.languages,
      }

    case SET_CURRENT_LANGUAGE:
      return {
        ...INITIAL_STATE,
        isLoggedIn: auth.isLoggedIn(),
      }

    case ACKNOWLEDGE_GLOBAL_MESSAGE:
      const newGlobalMessage =
        state.globalMessage === null
          ? null
          : {
              ...state.globalMessage,
              lastDisplayed: action.payload.lastDisplayed,
            }
      return {
        ...state,
        globalMessage: newGlobalMessage,
      }

    /** Privacy Policy */
    case FETCH_PRIVACY_POLICY_REQUEST:
      return {
        ...state,
        privacyPolicyReady: false,
        privacyPolicyError: false,
        privacyPolicyContent: '',
      }
    case FETCH_PRIVACY_POLICY_SUCCESS:
      return {
        ...state,
        privacyPolicyReady: true,
        privacyPolicyError: false,
        privacyPolicyContent: action.payload.privacyPolicy,
      }
    case FETCH_PRIVACY_POLICY_FAILURE:
      return {
        ...state,
        privacyPolicyReady: true,
        privacyPolicyError: true,
        privacyPolicyContent: '',
      }
    // Terms of use
    case FETCH_TERMS_OF_USE_REQUEST:
      return {
        ...state,
        termsOfUseReady: false,
        termsOfUseError: false,
        termsOfUseContent: '',
      }
    case FETCH_TERMS_OF_USE_SUCCESS:
      return {
        ...state,
        termsOfUseReady: true,
        termsOfUseError: false,
        termsOfUseContent: action.payload.termsOfUse,
      }
    case FETCH_TERMS_OF_USE_FAILURE:
      return {
        ...state,
        termsOfUseReady: true,
        termsOfUseError: true,
        termsOfUseContent: '',
      }
    case 'ACTION_SIDEBAR':
      return {
        ...state,
        sidebar: action.sidebar,
      }
    default:
      return state
  }
}

export default appReducer
